import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  keyframes,
} from "@mui/material"
import { t } from "i18next"
import Joi from "joi"
import React, { useCallback, useState } from "react"
import { ActionReq } from "shared/actionReq"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { selectCurrentTenant } from "src/features/me/meSlice"
import { useCreateThingsMutation } from "src/features/things/thingsApi"
import { v4 as uuid } from "uuid"
import { useAppSelector } from "../app/hooks"
import { selectLockerMetrics } from "../features/things/thingsSlice"
import FormFields, { FormField } from "./FormFields"
import { Sl2FormContainer } from "./Sl2FormContainer"

// check at localStorage first
const BTN_THRESHOLD = localStorage.getItem("order_locker_request_btn_threshold")
  ? parseFloat(localStorage.getItem("order_locker_request_btn_threshold")!)
  : 0.75

const joiSchema = Joi.object({
  ownerName: Joi.string().required(),
  email: Joi.string()
    .email({
      minDomainSegments: 2,
      tlds: { allow: false },
    })
    .required(),
  legalEntityName: Joi.string().required(),
  qtyNewLocker: Joi.number().integer().min(1).required(),
})
const OrderLockerRequestButton: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [formValues, setFormValues] = useState({
    ownerName: "",
    email: "",
    legalEntityName: "",
    qtyNewLocker: "",
  })
  const lockerMetrics = useAppSelector(selectLockerMetrics)
  const totalOccupancy = lockerMetrics.total.occupancy

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }))
  }

  const formFields: FormField[] = [
    {
      name: "ownerName",
      fieldType: "text",
      label: t("ownerName"),
      type: "text",
      required: true,
      fullWidth: true,
      size: "small",
    },
    {
      name: "email",
      fieldType: "text",
      label: t("email"),
      type: "email",
      required: true,
      fullWidth: true,
      size: "small",
    },
    {
      name: "legalEntityName",
      fieldType: "text",
      label: t("legalEntityName"),
      type: "text",
      required: true,
      fullWidth: true,
      size: "small",
    },
    {
      name: "qtyNewLocker",
      fieldType: "number",
      label: t("qtyNewLocker"),
      type: "number",
      required: true,
      fullWidth: true,
      size: "small",
    },
  ] as FormField[]

  const currentTenant = useAppSelector(selectCurrentTenant)

  const [error, setError] = useState<string | null>(null)

  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const [createThings] = useCreateThingsMutation()

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        setError(null)

        const actionReq: Partial<ActionReq> = {
          thingTypeMrn: ActionReqThingTypeMrn,
          executionMode: ActionReqExecutionModeEnum.sync,
          tenantId: currentTenant?.id,
          idempotencyKey: uuid(),
          actionType: ActionReqTypeEnum.OrderLockerRequest,
          inputData: {
            tenantId: currentTenant?.id,
            ownerName: data.ownerName,
            email: data.email,
            legalEntityName: data.legalEntityName,
            qtyNewLocker: data.qtyNewLocker,
          },
        }

        await createThings({
          body: { things: [actionReq] },
          params: { tenantId: currentTenant?.id },
        }).unwrap()
        setSuccessMessage(t("orderLockerRequestSuccess"))
      } catch (e) {
        console.error(e)
        setError(t("errorSubmittingRequest"))
        setSuccessMessage(null)
      }
    },
    [currentTenant],
  )

  if (totalOccupancy <= BTN_THRESHOLD) {
    return null
  }

  const pulse = keyframes`
    0% {
      box-shadow: 0 0 0 0 rgba(255, 189, 67, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(255, 189, 67, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 189, 67, 0);
    }
  `

  return (
    <Box>
      <Button
        variant="contained"
        sx={{
          textTransform: "none",
          backgroundColor: "#FFBD43",
          animation: `${pulse} 2s infinite`,
          "&:hover": {
            backgroundColor: "#FFBD43",
          },
        }}
        onClick={handleClickOpen}
      >
        {t("orderLockerRequest")}
      </Button>
      <Dialog data-ui="UI-SL2W-043" open={open} onClose={handleClose}>
        <Stack direction="column" spacing={1}>
          <DialogTitle>{t("orderLockerRequest")}</DialogTitle>
          <DialogContent>
            <Box mb={2} sx={{ width: "600px" }} />
            {!successMessage && (
              <Sl2FormContainer joiSchema={joiSchema} onSuccess={onSubmit}>
                <FormFields fields={formFields} defaultValues={formValues} />
                <Alert variant="outlined" severity="info" sx={{ mt: 4 }}>
                  {t("orderLockerRequestLegal")}
                </Alert>
                {error && (
                  <Alert variant="outlined" severity="error" sx={{ mt: 4 }}>
                    {t("orderLockerRequestError")}
                  </Alert>
                )}
                <Box py={2} />
                <DialogActions>
                  <Button onClick={handleClose} color="primary">
                    {t("cancel")}
                  </Button>
                  <Button type="submit" color="primary" variant="contained">
                    {t("send")}
                  </Button>
                </DialogActions>
              </Sl2FormContainer>
            )}
            {successMessage && (
              <Stack direction="column" spacing={1}>
                <Alert variant="outlined" severity="success" sx={{ mt: 4 }}>
                  {successMessage}
                </Alert>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Button onClick={handleClose} color="primary">
                    {t("close")}
                  </Button>
                </Stack>
              </Stack>
            )}
          </DialogContent>
        </Stack>
      </Dialog>
    </Box>
  )
}

export default OrderLockerRequestButton
